/* Pure cart rules. Shopify remains authoritative for actual prices and discounts. */
((scope) => {
  const rules = typeof module !== 'undefined' && module.exports ? require('./v2-commerce-rules.js') : scope.MettaV2Rules;
  const id = value => String(value ?? '').split('/').pop();
  const planId = item => id(item.selling_plan_allocation?.selling_plan?.id);
  const propsKey = props => JSON.stringify(Object.entries(props || {}).sort(([a], [b]) => a.localeCompare(b)));
  const identity = item => `${id(item.variant_id || item.id)}|${propsKey(item.properties)}`;
  const bundleId = item => String(item.properties?._bundleId || '');
  // Bundle lines never share a group with loose lines, so plan changes on the rest stay possible.
  const groupKey = item => `${item.properties?._dynamicBoxId || ''}|${item.properties?._dynamicBoxIndex || ''}|${planId(item)}${bundleId(item) ? `|${bundleId(item)}` : ''}`;
  const volume = (config, count) => Object.entries(config?.percentDiscountTier || {}).reduce((best, [min, pct]) => count >= +min ? Math.max(best, +pct) : best, 0);
  const plans = box => (box?.config?.sellingPlanGroup || []).filter(p => String(p.interval).toUpperCase() === 'WEEK' && +p.intervalCount > 0).sort((a, b) => +a.position - +b.position);
  const allocation = (product, plan) => product?.allocations?.find(a => id(a.id) === id(plan));
  function catalog(state) {
    const products = new Map();
    for (const box of state.boxes || []) for (const product of box.products || []) if (!products.has(id(product.id))) products.set(id(product.id), product);
    return products;
  }
  function groups(state) {
    const map = new Map(), products = catalog(state);
    for (const item of state.cart.items) {
      const key = groupKey(item), p = products.get(id(item.variant_id));
      if (!map.has(key)) map.set(key, { key, planId: planId(item), box: state.boxes.find(b => b.id && b.id === item.properties?._dynamicBoxId), items: [] });
      map.get(key).items.push({ ...item, meta: state.lines.find(l => l.key === item.key), product: p });
    }
    return [...map.values()];
  }
  // Locked bundles (startpaket): every line of one added package, sold and removed whole.
  function bundles(state) {
    const map = new Map();
    for (const item of state.cart.items) {
      const key = bundleId(item); if (!key) continue;
      if (!map.has(key)) map.set(key, { id: key, title: item.properties._bundle || '', code: String(item.properties._bundleCode || ''), planId: '', items: [] });
      const bundle = map.get(key);
      bundle.planId ||= planId(item);
      bundle.items.push({ ...item, meta: state.lines.find(l => l.key === item.key) });
    }
    return [...map.values()];
  }
  function options(group, commerce) {
    // Explicit box identity is essential: never manufacture a new subscription box.
    if (!group.box || !group.items.every(i => i.properties?._dynamicBoxIndex != null)) return [];
    const available = plans(group.box).filter(p => group.items.every(i => allocation(i.meta, p.gid)));
    return commerce ? (rules?.allowedPlans(available, commerce) || []) : available;
  }
  function canChange(state, group, commerce) {
    if (group.items.some(bundleId) || !options(group, commerce).length) return false;
    const identities = group.items.map(identity);
    // A plan change must not merge with another group or ambiguous duplicate row.
    return identities.every(k => state.cart.items.filter(i => identity(i) === k).length === 1);
  }
  function shipping(state, config, total = state.cart.total_price) {
    if (['active', 'preview'].includes(config.commerceMode)) return shippingRates(state, config, total)?.dhl ?? null;
    if (!state.cart.requires_shipping) return 0;
    if (state.country !== 'SE' || state.cart.currency !== 'SEK') return null;
    return total >= config.threshold ? 0 : config.fee;
  }
  function shippingRates(state, config, total = state.cart.total_price) {
    if (!rules || !['active', 'preview'].includes(config.commerceMode) || !config.commerce) return null;
    return rules.shipping(total, config.commerce, { country: state.country, currency: state.cart.currency, requiresShipping: state.cart.requires_shipping });
  }
  function breakdown(state) {
    let regular = 0, subscription = 0;
    const discounts = new Map();
    const add = (app, amount) => {
      if (!app || !amount) return;
      const key = app.key || `${app.type}:${app.title}`;
      const entry = discounts.get(key) || { title: app.title, type: app.type, amount: 0 };
      entry.amount += amount; discounts.set(key, entry);
    };
    for (const item of state.cart.items) {
      const meta = state.lines.find(l => l.key === item.key);
      const base = meta?.base ?? item.original_price;
      regular += Math.max(base, item.original_price) * item.quantity;
      if (planId(item)) subscription += Math.max(0, base - (allocation(meta, planId(item))?.price ?? item.original_price)) * item.quantity;
      for (const a of item.line_level_discount_allocations || []) add(a.discount_application, a.amount);
    }
    for (const app of state.cart.cart_level_discount_applications || []) add(app, app.total_allocated_amount);
    const saved = Math.max(0, regular - state.cart.total_price);
    subscription = Math.min(subscription, saved);
    const explicit = [...discounts.values()];
    const residual = saved - subscription - explicit.reduce((s, a) => s + a.amount, 0);
    // If upstream allocations overlap, show an exact combined residual instead of double-counting.
    return { regular, saved, subscription, discounts: residual >= 0 ? explicit : [], other: residual >= 0 ? residual : saved - subscription };
  }
  function codes(state) {
    const result = new Map();
    for (const d of breakdown(state).discounts.filter(d => d.type === 'discount_code')) result.set(d.title.toLowerCase(), { code: d.title, applicable: true, amount: d.amount });
    for (const d of state.cart.discount_codes || []) {
      const key = d.code.toLowerCase(); result.set(key, { ...result.get(key), ...d });
    }
    return [...result.values()];
  }
  function quoteGroup(group, targetPlan) {
    const count = group.items.reduce((s, i) => s + i.quantity, 0);
    if (group.box?.config?.oneBox) {
      const quote = rules.quote(group.items.map(item => ({ id: item.key, quantity: item.quantity,
        unitPrice: targetPlan ? allocation(item.meta, targetPlan)?.price : item.meta?.base,
        fullPrice: item.meta?.base, eligible: true })), group.box.config.commerce, { currency: group.box.config.currencyCode });
      return quote.error ? null : quote.total;
    }
    const discount = volume(group.box?.config, count);
    let total = 0;
    for (const item of group.items) {
      const base = targetPlan ? allocation(item.meta, targetPlan)?.price : item.meta?.base;
      if (!Number.isFinite(base)) return null;
      total += Math.round(base * (1 - discount / 100)) * item.quantity;
    }
    return total;
  }
  function quoteCart(state, commerce, { groupKey: targetGroupKey, targetPlan, extra } = {}) {
    const fail = error => ({ error, subtotal: null, eligibleSubtotal: null, discountPercent: null, discountAmount: null, total: null, lines: [], remaining: null });
    if (!rules || !Array.isArray(commerce?.eligibleProducts) || !commerce.eligibleProducts.length
      || !commerce.eligibleProducts.every(productId => /^\d+$/.test(String(productId)))) return fail('config');
    if (codes(state).length
      || (state.cart.cart_level_discount_applications || []).some(discount => discount.type === 'discount_code')
      || state.cart.items.some(item => (item.line_level_discount_allocations || []).some(discount => discount.discount_application?.type === 'discount_code'))) return fail('codes');
    const eligibleProducts = new Set(commerce.eligibleProducts.map(id)), rows = [], gs = groups(state);
    const isEligible = item => (!commerce.boxId || item.properties?._dynamicBoxId === commerce.boxId) && eligibleProducts.has(id(item.product_id || item.meta?.productId || item.product?.productId));
    // An automatic discount's future eligibility cannot be inferred from today's total.
    // Skio's base selling-plan prices remain in the allocation metadata below.
    const hasAutomaticDiscounts = (state.cart.cart_level_discount_applications || []).some(discount => discount.total_allocated_amount > 0)
      || state.cart.items.some(item => (item.line_level_discount_allocations || []).some(discount => discount.amount > 0));
    if (hasAutomaticDiscounts) {
      // A separately labelled preview may replace a fully reconciled legacy tier.
      // This verifies the old total only; old percentages never enter the new quote.
      const understoodPreview = commerce.preview === true
        && state.cart.items.reduce((sum, item) => sum + item.final_line_price, 0) === state.cart.total_price
        && gs.every(group => group.items.some(isEligible)
          ? group.items.every(isEligible) && group.box && quoteGroup(group, group.planId) === group.items.reduce((sum, item) => sum + item.final_line_price, 0)
          : group.items.every(item => !(item.line_level_discount_allocations || []).some(discount => discount.amount > 0)));
      if (!understoodPreview) return fail('discounts');
    }
    if (targetGroupKey !== undefined && !gs.some(group => group.key === targetGroupKey)) return fail('group');
    for (const group of gs) {
      const selectedPlan = group.key === targetGroupKey && targetPlan !== undefined ? id(targetPlan) : group.planId;
      for (const item of group.items) {
        const productId = id(item.product_id || item.meta?.productId || item.product?.productId);
        if (!productId) return fail('product');
        const eligible = isEligible(item);
        let unitPrice;
        if (eligible || (group.key === targetGroupKey && targetPlan !== undefined)) unitPrice = selectedPlan ? (allocation(item.meta, selectedPlan) || allocation(item.product, selectedPlan))?.price : item.meta?.base;
        else unitPrice = item.quantity === 0 && item.final_line_price === 0 ? 0 : item.final_line_price / item.quantity;
        if (!Number.isSafeInteger(unitPrice) || unitPrice < 0) return fail('price');
        rows.push({ id: item.key, quantity: item.quantity, unitPrice, fullPrice: item.meta?.base, eligible });
      }
    }
    if (extra) {
      const group = gs.find(candidate => candidate.key === extra.groupKey), product = extra.product;
      if (!group || !product?.productId) return fail('group');
      const selectedPlan = group.key === targetGroupKey && targetPlan !== undefined ? id(targetPlan) : group.planId;
      const unitPrice = selectedPlan ? allocation(product, selectedPlan)?.price : product.price;
      if (!Number.isSafeInteger(unitPrice) || unitPrice < 0) return fail('price');
      rows.push({ id: `extra:${id(product.id)}`, quantity: 1, unitPrice, fullPrice: product.price, eligible: (!commerce.boxId || group.box?.id === commerce.boxId) && eligibleProducts.has(id(product.productId)) });
    }
    return rules.quote(rows, commerce, { currency: state.cart.currency });
  }
  function recommendation(state, config, dismissed = {}) {
    const gs = groups(state);
    const modern = ['active', 'preview'].includes(config.commerceMode), commerce = config.commerce;
    // Hypothetical savings are only safe once the active rules reconcile the whole cart.
    if (modern) {
      const current = quoteCart(state, commerce);
      if (current.error || current.total !== state.cart.total_price) return null;
    }
    // Codes may have eligibility/combination rules unavailable to the theme. Do not promise an unverified benefit.
    if (codes(state).length) return null;
    const plain = gs.filter(g => !g.planId && canChange(state, g, commerce));
    if (config.upgrade && plain.length === 1 && !gs.some(g => g.planId) && !dismissed.upgrade) {
      const group = plain[0], plan = options(group, commerce)[0];
      const old = modern ? state.cart.total_price : group.items.reduce((s, i) => s + i.final_line_price, 0);
      const expected = modern ? quoteCart(state, commerce).total : quoteGroup(group, null);
      const total = modern ? quoteCart(state, commerce, { groupKey: group.key, targetPlan: id(plan.gid) }).total : quoteGroup(group, id(plan.gid));
      // Offers only for understood pricing: custom codes/bundles must not be silently lost.
      if (expected === old && total != null && total < old) {
        const percent = Math.round((1 - total / old) * 100);
        return { type: 'upgrade', group, plan, percent, total: state.cart.total_price - old + total };
      }
    }
    const threshold = modern ? commerce?.shipping?.threshold : config.threshold;
    if (!config.recommend || dismissed.extra || state.cart.total_price >= threshold || shipping(state, config) == null || gs.length !== 1) return null;
    const group = gs[0];
    if (!group.box || !canChange(state, group, commerce) || (!modern && quoteGroup(group, group.planId) !== state.cart.total_price)) return null;
    const families = new Set(group.items.map(i => i.product?.family));
    if (families.size !== 1 || families.has(undefined)) return null;
    const count = group.items.reduce((s, i) => s + i.quantity, 0);
    if (!group.box.config?.oneBox && count >= Number(group.box.config?.sizeRange?.[1])) return null;
    const presentProducts = new Set(gs.flatMap(g => g.items).map(i => id(i.product?.productId)));
    const products = [...catalog(state).values()].filter(p => p.available && p.meals > 0 && families.has(p.family) && !presentProducts.has(id(p.productId)) && !state.cart.items.some(i => id(i.variant_id) === id(p.id)) && (!group.planId || allocation(p, group.planId)));
    const sales = config.sales || {};
    const priority = (config.recommendations?.length ? config.recommendations : sales.priority || []).map(id);
    const ranking = (sales.ranking || []).map(id);
    if (!priority.length && !ranking.length) return null;
    const present = [...new Set(group.items.map(i => id(i.product?.productId)))];
    const position = (list, value) => { const index = list.indexOf(id(value)); return index < 0 ? Infinity : index; };
    const affinity = product => present.reduce((sum, source) => {
      const index = position((sales.related?.[source] || []).map(id), product.productId);
      return sum + (Number.isFinite(index) ? 1 / (index + 1) : 0);
    }, 0);
    products.sort((a, b) => {
      const pinnedA = position(priority, a.productId), pinnedB = position(priority, b.productId);
      if (pinnedA !== pinnedB) return pinnedA < pinnedB ? -1 : 1;
      const related = affinity(b) - affinity(a);
      if (related) return related;
      const rankA = position(ranking, a.productId), rankB = position(ranking, b.productId);
      if (rankA !== rankB) return rankA < rankB ? -1 : 1;
      return id(a.id).localeCompare(id(b.id));
    });
    for (const product of products) {
      const synthetic = { ...group, items: [...group.items, { quantity: 1, meta: { base: product.price, allocations: product.allocations } }] };
      const total = modern ? quoteCart(state, commerce, { extra: { groupKey: group.key, product } }).total : quoteGroup(synthetic, group.planId);
      if (total != null && total >= threshold) return { type: 'extra', group, product, total };
    }
    return null;
  }
  const api = { id, identity, groupKey, bundleId, planId, volume, plans, allocation, catalog, groups, bundles, options, canChange, shipping, shippingRates, breakdown, codes, quoteGroup, quoteCart, recommendation };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else scope.MettaCartModel = api;
})(typeof window === 'undefined' ? globalThis : window);
